import { ComposeSMSParams } from '../../params/redux/sms';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { errorHandler } from '../../helper/error_handler';
import { isErrorWithMessage } from '../../helper';
import { resettingSMSLoader } from '../slices/sms';
import { RootState } from '../../redux/store';
import { SendBulkEmailsParams } from '../../params/redux/email';
import SMSApi from '../../apis/sms';

export const fetchUserSMSThunk = createAsyncThunk(
    'sms/fetch',
    async (client_id: number, { dispatch, rejectWithValue, getState }): Promise<unknown> => {
        try {
            const state = getState() as RootState;
            const response = await SMSApi.fetchUserSMS(
                state.app.token,
                client_id,
                 location.pathname.includes('/app/leads') || location.pathname.includes('/lead-management') ? null : state.deal.selectedDeal?.id || null,
                !location.pathname.includes('/app/leads') && !location.pathname.includes('/lead-management') ? null : state.lead.selectedLead?.id || null
            );
            if (response?.success === 1) {
                return response?.messages;
            }
            return errorHandler(response?.msg as string || 'Request Failed', dispatch, rejectWithValue);
        } catch (error) {
            if (isErrorWithMessage(error)) {
                return errorHandler(error.message, dispatch, rejectWithValue);
            }
            return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
        } finally {
            dispatch(resettingSMSLoader());
        }
    }
);

export const fetchUserSMSIntegrationThunk = createAsyncThunk(
    'sms/fetch-integration',
    async (client_id: number, { dispatch, rejectWithValue, getState }): Promise<unknown> => {
        try {
            const state = getState() as RootState;
            const response = await SMSApi.fetchUserIntegration(state.app.token, client_id);
            if (response?.success === 1) {
                return response?.integrations;
            }
            return errorHandler(response?.msg as string || 'Request Failed', dispatch, rejectWithValue);
        } catch (error) {
            if (isErrorWithMessage(error)) {
                return errorHandler(error.message, dispatch, rejectWithValue);
            }
            return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
        } finally {
            dispatch(resettingSMSLoader());
        }
    }
);

export const fetchSMSMessagesThunk = createAsyncThunk(
    'sms/sms-messages',
    async (client_id: number, { dispatch, rejectWithValue, getState }): Promise<unknown> => {
        try {
            const state = getState() as RootState;
            const response = await SMSApi.fetchSMSMessages(
                state.app.token,
                state.sms.selectedSms?.id as number,
                client_id
            );
            if (response?.success === 1) {
                return response?.messages;
            }
            return errorHandler(response?.msg as string || 'Request Failed', dispatch, rejectWithValue);
        } catch (error) {
            if (isErrorWithMessage(error)) {
                return errorHandler(error.message, dispatch, rejectWithValue);
            }
            return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
        } finally {
            dispatch(resettingSMSLoader());
        }
    }
);

export const composeSMSThunk = createAsyncThunk(
    'sms/compose',
    async (dataComing: ComposeSMSParams, { dispatch, rejectWithValue, getState }): Promise<unknown> => {
        try {
            const state = getState() as RootState;
            const data = {
                ...dataComing,
                client_id: dataComing.client_id as number,
                deal_id: location.pathname.includes('/app/leads') || location.pathname.includes('/lead-management') ? null : state.deal.selectedDeal?.id || null,
                lead_id: !location.pathname.includes('/app/leads') && !location.pathname.includes('/lead-management') ? null : state.lead.selectedLead?.id || null,
                message_id: state.sms.selectedSms?.id || 0
            };
            const response = await SMSApi.composeSMS(state.app.token, data);
            if (response?.success === 1) {
                await dispatch(fetchUserSMSThunk(dataComing.client_id as number)).unwrap();
                if (state.sms.selectedSms && state.sms.selectedSms?.id) {
                    await dispatch(fetchSMSMessagesThunk(dataComing.client_id as number)).unwrap();
                }
                return true;
            }
            return errorHandler(response?.msg as string || 'Request Failed', dispatch, rejectWithValue);
        } catch (error) {
            if (isErrorWithMessage(error)) {
                return errorHandler(error.message, dispatch, rejectWithValue);
            }
            return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
        } finally {
            dispatch(resettingSMSLoader());
        }
    }
);

export const replyToSMSThreadThunk = createAsyncThunk(
    'sms/reply-to-thread',
    async (data: ComposeSMSParams, { dispatch, rejectWithValue, getState }): Promise<unknown> => {
        try {
            const state = getState() as RootState;
            const response = await SMSApi.replyToThread(state.app.token, data);
            if (response?.success === 1) {
                await dispatch(fetchSMSMessagesThunk(data.client_id || 0)).unwrap();
                return true;
            }
            return errorHandler(response?.msg as string || 'Request Failed', dispatch, rejectWithValue);
        } catch (error) {
            if (isErrorWithMessage(error)) {
                return errorHandler(error.message, dispatch, rejectWithValue);
            }
            return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
        } finally {
            dispatch(resettingSMSLoader());
        }
    }
);

export const markAllSMSAsReadThunk = createAsyncThunk(
    'sms/mark-read',
    async (_, { dispatch, rejectWithValue, getState }): Promise<unknown> => {
        try {
            const state = getState() as RootState;
            const response = await SMSApi.markAllSMSAsRead(state.app.token);
            if (response?.success === 1) {
                return true;
            }
            return errorHandler(response?.msg as string || 'Request Failed', dispatch, rejectWithValue);
        } catch (error) {
            if (isErrorWithMessage(error)) {
                return errorHandler(error.message, dispatch, rejectWithValue);
            }
            return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
        } finally {
            dispatch(resettingSMSLoader());
        }
    }
);

export const deleteSMSThunk = createAsyncThunk(
    'sms/delete',
    async (_, { dispatch, rejectWithValue, getState }): Promise<unknown> => {
        try {
            const state = getState() as RootState;
            const response = await SMSApi.deleteSMS(state.app.token);
            if (response?.success === 1) {
                return true;
            }
            return errorHandler(response?.msg as string || 'Request Failed', dispatch, rejectWithValue);
        } catch (error) {
            if (isErrorWithMessage(error)) {
                return errorHandler(error.message, dispatch, rejectWithValue);
            }
            return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
        } finally {
            dispatch(resettingSMSLoader());
        }
    }
);

export const fetchTwilioTokenThunk = createAsyncThunk(
    'sms/twilio-token',
    async (client_id: number, { dispatch, rejectWithValue, getState }): Promise<unknown> => {
        try {
            const state = getState() as RootState;
            const response = await SMSApi.twilioCallToken(state.app.token, client_id);
            if (response?.success === 1) {
                return response.token;
            }
            return errorHandler(response?.msg as string || 'Request Failed', dispatch, rejectWithValue);
        } catch (error) {
            if (isErrorWithMessage(error)) {
                return errorHandler(error.message, dispatch, rejectWithValue);
            }
            return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
        } finally {
            dispatch(resettingSMSLoader());
        }
    }
);

export const sendBulkSMSThunk = createAsyncThunk(
    'sms/bulk',
    async (data: SendBulkEmailsParams, { dispatch, rejectWithValue, getState }): Promise<unknown> => {
        try {
            const state = getState() as RootState;
            const response = await SMSApi.sendBulkSMS(state.app.token, data);
            if (response?.success === 1) {
                return true;
            }
            return errorHandler(response?.msg as string || 'Request Failed', dispatch, rejectWithValue);
        } catch (error) {
            if (isErrorWithMessage(error)) {
                return errorHandler(error.message, dispatch, rejectWithValue);
            }
            return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
        } finally {
            dispatch(resettingSMSLoader());
        }
    }
);